import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const parseOrThrow = async (response, fallbackMessage) => {
	if (response.ok) return response.json()
	let message = fallbackMessage
	try {
		const payload = await response.json()
		if (payload?.message) message = payload.message
	} catch {}
	throw new Error(message)
}

// ======================================================
// Messaging Actions (Experience-level endpoints)
// ======================================================

export const listBlasts = async (orgSlug, experienceSlug, { signal } = {}) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts`,
		{ headers: getAuthHeaders(), signal }
	)
	return parseOrThrow(r, 'Failed to fetch blasts')
}

export const createBlast = async (orgSlug, experienceSlug, data) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
			body: JSON.stringify(data),
		}
	)
	return parseOrThrow(r, 'Failed to create blast')
}

export const updateBlast = async (orgSlug, experienceSlug, id, data) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}`,
		{
			method: 'PUT',
			headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
			body: JSON.stringify(data),
		}
	)
	return parseOrThrow(r, 'Failed to update blast')
}

export const deleteBlast = async (orgSlug, experienceSlug, id) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}`,
		{ method: 'DELETE', headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to delete blast')
}

export const cancelScheduledBlast = async (
	orgSlug,
	experienceSlug,
	id,
	{ action = 'draft' } = {}
) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}/cancel`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
			body: JSON.stringify({ action }),
		}
	)
	return parseOrThrow(r, 'Failed to cancel scheduled blast')
}

export const getBlast = async (orgSlug, experienceSlug, id) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}`,
		{ headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to fetch blast')
}

export const sendBlast = async (orgSlug, experienceSlug, id, data = null) => {
	const hasPayload = !!data && typeof data === 'object'
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}/send`,
		{
			method: 'POST',
			headers: hasPayload
				? { 'Content-Type': 'application/json', ...getAuthHeaders() }
				: getAuthHeaders(),
			...(hasPayload ? { body: JSON.stringify(data) } : {}),
		}
	)
	return parseOrThrow(r, 'Failed to send blast')
}

export const sendBlastTest = async (orgSlug, experienceSlug, data) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/test`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
			body: JSON.stringify(data || {}),
		}
	)
	return parseOrThrow(r, 'Failed to send blast test')
}

export const listRecipients = async (
	orgSlug,
	experienceSlug,
	blastId,
	{ status, search, cursor, page, take = 50 } = {}
) => {
	const qs = new URLSearchParams({
		blastId,
		...(status ? { status } : {}),
		...(search ? { search } : {}),
		...(cursor ? { cursor } : {}),
		...(Number.isFinite(Number(page)) && Number(page) > 0
			? { page: Math.floor(Number(page)) }
			: {}),
		take,
	})
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/recipients?${qs.toString()}`,
		{ headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to fetch recipients')
}

export const getBlastStats = async (orgSlug, experienceSlug, id) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}/stats`,
		{ headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to fetch stats')
}

export const listAllRecipients = async (
	orgSlug,
	experienceSlug,
	blastId,
	{ status, search } = {},
) => {
	const rows = []
	let cursor
	do {
		const result = await listRecipients(orgSlug, experienceSlug, blastId, {
			status, search, cursor, take: 200,
		})
		rows.push(...result.recipients)
		cursor = result.nextCursor
	} while (cursor)
	return rows
}

export const getBlastQueueHealth = async (orgSlug, experienceSlug, id) => {
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}/queue-health`,
		{ headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to fetch queue health')
}

export const listCheckInMessages = async (
	orgSlug,
	experienceSlug,
	{ status, search, page, take = 50 } = {}
) => {
	const qs = new URLSearchParams({
		...(status ? { status } : {}),
		...(search ? { search } : {}),
		...(Number.isFinite(Number(page)) && Number(page) > 0
			? { page: Math.floor(Number(page)) }
			: {}),
		take,
	})
	const r = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/check-in-messages?${qs.toString()}`,
		{ headers: getAuthHeaders() }
	)
	return parseOrThrow(r, 'Failed to fetch check-in messages')
}

export const sendTestEmail = async (
	orgSlug,
	experienceSlug,
	optionsOrMessageType
) => {
	const payload =
		typeof optionsOrMessageType === 'string'
			? { messageType: optionsOrMessageType }
			: optionsOrMessageType || {}

	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/sendmail`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		}
	)
	return parseOrThrow(res, 'Failed to send test email')
}

export const sendTestSms = async (
	orgSlug,
	experienceSlug,
	optionsOrMessageType
) => {
	const payload =
		typeof optionsOrMessageType === 'string'
			? { messageType: optionsOrMessageType }
			: optionsOrMessageType || {}

	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/messaging/sendsms`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		}
	)
	return parseOrThrow(res, 'Failed to send test SMS')
}
